/**
 * Browser host for the simulation. Owns the frame loop and everything that is
 * presentation or platform: pointer lock, pause, interpolation, HUD,
 * settings, diagnostics. The simulation never reads wall-clock time; this
 * class converts frames into whole fixed ticks via FixedStepClock.
 *
 * Interruption policy (see ARCHITECTURE.md):
 *  - Pause (pointer lock lost / Esc) stops ticking. A running full-run attempt
 *    is invalidated; practice attempts simply wait.
 *  - A stall (frame gap > stallThresholdMs, or more backlog than the catch-up
 *    budget) is reported: the excess time is discarded, never simulated in a
 *    burst, and a running full-run attempt is invalidated.
 */
import type * as THREE from 'three';
import { makeConfig, type EngineConfig, type MovementConfig } from './Config';
import { FixedStepClock, tickInputDeadline } from './Clock';
import { InputManager, type Bindings, type CommandAction } from '../input/InputManager';
import { makeInput, type InputFrame } from '../input/InputFrame';
import { initPhysics } from '../simulation/PhysicsWorld';
import { GAME_RESPAWN_DELAY_TICKS, Simulation } from '../simulation/Simulation';
import { loadLevel } from '../world/LevelLoader';
import type { LevelData, SpawnPoint } from '../world/LevelData';
import { LocalRecordStore, type RecordStore } from '../gameplay/RecordStore';
import { EXPERIMENTS, createRuleSet, isExperimentEnabled, setExperimentEnabled, type ExperimentInfo } from '../movement/experiments';
import { Renderer, type RenderStats } from '../rendering/Renderer';
import { SceneView } from '../rendering/SceneView';
import { QUALITY } from '../rendering/Quality';
import { FirstPersonCamera } from '../camera/FirstPersonCamera';
import { PerfMonitor, type PerfSnapshot } from '../debug/PerfMonitor';
import { PhysicsDebugView } from '../debug/PhysicsDebugView';
import { DebugOverlay } from '../debug/DebugOverlay';
import { FrameTrace, type FrameSample } from '../debug/FrameTrace';
import { Hud, formatTime, type HudView } from '../ui/Hud';
import { loadSettings, rollDegrees, saveSettings, type Settings } from '../ui/Settings';
import { startRefusedText, type GameMode } from '../gameplay/AttemptManager';
import type { MovementRuleSet } from '../movement/MovementRules';
import { DevTools, type DevHost } from '../devtools/DevTools';
import { DevPanel } from '../devtools/DevPanel';

const DEV_BOOST_SPEED = 15;
const MAX_FRAME_DT = 0.1;
/** How fast the camera follows a posture change on the ground (1/s). */
const EYE_RATE = 18;

export interface EngineDom {
  canvas: HTMLCanvasElement;
  hud: HTMLElement;
  flash: HTMLElement;
  debug: HTMLElement;
  /** Container the developer panel is appended to. */
  app: HTMLElement;
  /** The developer harness's pause overlay. A game host passes `onPauseChange` instead. */
  overlay?: HTMLElement;
  status?: HTMLElement;
  resume?: HTMLButtonElement;
  experiments?: HTMLElement;
  inputs?: { sensitivity: HTMLInputElement; fov: HTMLInputElement; scale: HTMLInputElement; roll: HTMLInputElement };
}

/** Read-only view of engine state for automated browser tests (dev builds only). */
export interface EngineTestHooks {
  state(): { tick: number; position: [number, number, number]; speed: number; grounded: boolean; alive: boolean; paused: boolean; mode: string; status: string; ticksRun: number; stalls: number };
  forceResume(): void;
  /** Pauses as a pointer unlock would (headless tests cannot lock, so they cannot unlock either). */
  forcePause(): void;
  setYaw(yaw: number, pitch?: number): void;
  perf(): PerfSnapshot & RenderStats;
  frameTrace(): FrameSample[];
  clearFrameTrace(): void;
  pointerLocked(): boolean;
  /** View angles and sensitivity now, and the yaw the simulation consumed at its latest tick. */
  look(): { yaw: number; pitch: number; sensitivity: number; simYaw: number; tick: number };
  /** Places the player (feet) with a velocity; used for repeatable browser scenarios. */
  teleport(position: [number, number, number], velocity?: [number, number, number]): void;
  /** Live movement configuration (changes with developer-panel tuning). */
  movement(): MovementConfig;
  waypoints(): { name: string; spawn: SpawnPoint }[];
  /**
   * Restarts the attempt at the track start, then feeds these inputs to the
   * following ticks in place of live input (one per tick), so a browser test
   * can run a route recorded in Node through the real frame loop.
   *
   * `lockstep` (ticks per rendered frame) decouples the script from wall-clock
   * time: each frame runs exactly that many ticks, so a slow or loaded test
   * machine cannot stall the run. Simulation time stays the authority (the
   * same ticks run either way); only the real-time pacing, and with it the
   * stall check, is skipped while the script lasts. Development builds only.
   */
  playInputs(frames: readonly InputFrame[], options?: { lockstep?: number }): void;
  /** Scripted inputs not yet consumed. */
  scriptedRemaining(): number;
  /** What the graphics preset produced: MSAA, pixel ratio, GPU. */
  graphics(): { antialias: boolean; pixelRatio: number; gpu: string };
  /** GPU ms per rendered frame since the last clearFrameTrace (needs `?gputime=1`; null where the browser has no timer queries). */
  gpuTimes(): number[] | null;
}

export interface EngineOptions {
  /** Dev only: cap rendering to this many frames per second (0 = display rate). */
  fpsLimit?: number;
  /** Where best times go (default: localStorage). Editor playtests pass a store that is thrown away. */
  records?: RecordStore;
  /** Called after every simulation tick (playtest telemetry). Must not change the simulation. */
  onTick?: (sim: Simulation) => void;
  /** Called once per rendered frame before drawing, with the interpolation point (ghosts). Must not change the simulation. */
  onRender?: (alpha: number) => void;
  /** Editor playtest: a banner in the overlay and a way back. */
  playtest?: { banner: string; onExit: () => void };
  /** Initial settings (default: the stored ones). */
  settings?: Settings;
  /** Key bindings (default: the built-in table). */
  bindings?: Readonly<Bindings>;
  /** Developer tools (F2 panel, lab spawn, boost). Off in the game, where they could alter a record run. */
  devTools?: boolean;
  /** Replaces the text HUD (the game's HUD). */
  hud?: HudView;
  /** The host draws its own pause screen: called on every pause and resume instead of showing `dom.overlay`. */
  onPauseChange?: (paused: boolean, message: string) => void;
  /** Mode the first attempt starts in (default: practice). */
  startMode?: GameMode;
}

export class Engine implements DevHost {
  readonly sim: Simulation;
  readonly rules: MovementRuleSet;
  private readonly dev: DevTools;
  private readonly devPanel: DevPanel | null;
  private readonly devToolsEnabled: boolean;
  private readonly onPauseChange: EngineOptions['onPauseChange'];
  private readonly clock: FixedStepClock;
  private readonly renderer: Renderer;
  private readonly scene: SceneView;
  private readonly camera: FirstPersonCamera;
  private readonly input: InputManager;
  private readonly perf = new PerfMonitor();
  private readonly physicsView: PhysicsDebugView;
  private readonly debugOverlay: DebugOverlay;
  private readonly hud: HudView;
  private readonly frame: InputFrame = makeInput();
  /** Test-only scripted input (see EngineTestHooks.playInputs). */
  private scripted: readonly InputFrame[] = [];
  /** Test-only: ticks per frame while a scripted run is in lockstep (0 = real time). */
  private lockstepTicks = 0;
  private eyeHeight = 0;
  private scriptCursor = 0;
  private readonly eye = { x: 0, y: 0, z: 0 };
  private settings: Settings;
  private paused = true;
  private debugVisible = false;
  private lastFrameMs = -1;
  private ticksRun = 0;
  private rafId = 0;
  private readonly resizeObserver: ResizeObserver;
  private readonly unsubscribe: (() => void)[] = [];
  private readonly trace = new FrameTrace(1200);
  private readonly fpsLimit: number;
  private nextFrameAt = 0;
  private vectorsVisible = false;
  /** Removes every listener this engine added to DOM it does not own. */
  private readonly uiAbort = new AbortController();
  private readonly onTick: ((sim: Simulation) => void) | undefined;
  private readonly onRender: ((alpha: number) => void) | undefined;
  private readonly playtest: EngineOptions['playtest'];

  static async create(dom: EngineDom, level: LevelData, options: EngineOptions = {}): Promise<Engine> {
    await initPhysics();
    return new Engine(dom, level, makeConfig(), options);
  }

  private constructor(
    private readonly dom: EngineDom,
    level: LevelData,
    private readonly config: Readonly<EngineConfig>,
    options: EngineOptions,
  ) {
    this.fpsLimit = Math.max(0, options.fpsLimit ?? 0);
    this.onTick = options.onTick;
    this.onRender = options.onRender;
    this.playtest = options.playtest;
    const loaded = loadLevel(level);
    this.rules = createRuleSet();
    try {
      this.sim = new Simulation(loaded, config, { rules: this.rules, records: options.records ?? new LocalRecordStore(), respawnDelayTicks: GAME_RESPAWN_DELAY_TICKS });
    } catch (err: unknown) {
      // An unsafe spawn is refused here; the physics world is already allocated in WASM memory.
      loaded.physics.dispose();
      throw err;
    }
    const sc = config.simulation;
    this.clock = new FixedStepClock(sc.tickRate, sc.maxCatchUpTicks, sc.stallThresholdMs);

    this.devToolsEnabled = options.devTools ?? true;
    this.onPauseChange = options.onPauseChange;
    this.settings = options.settings ?? loadSettings();
    const quality = QUALITY[this.settings.graphics];
    let renderer: Renderer | undefined;
    try {
      renderer = new Renderer(dom.canvas, { antialias: quality.antialias, maxPixelRatio: quality.maxPixelRatio });
      this.renderer = renderer;
      this.renderer.setAtmosphere(level.atmosphere ?? 'violet-dusk');
      this.scene = new SceneView(level, loaded.materials, { highContrast: this.settings.highContrast, outlineColor: this.settings.outlineColor });
    } catch (err: unknown) {
      // Release what was created before the failure: the WebGL context (if any) and the physics world.
      renderer?.dispose();
      loaded.physics.dispose();
      throw err;
    }
    this.renderer.scene.add(this.scene.group);
    this.physicsView = new PhysicsDebugView(loaded.physics);
    this.physicsView.visible = false;
    this.renderer.scene.add(this.physicsView.group);
    this.camera = new FirstPersonCamera({ fovDeg: this.settings.fovDeg, rollDeg: rollDegrees(this.settings) });
    this.debugOverlay = new DebugOverlay(dom.debug);
    this.hud = options.hud ?? new Hud(dom.hud, dom.flash);

    this.input = new InputManager(dom.canvas, {
      onCommand: (a) => this.onCommand(a),
      onLockChange: (locked) => (locked ? this.resume() : this.pause('paused')),
      onLockError: (msg) => this.showOverlay(msg),
    }, options.bindings);
    this.input.sensitivity = this.settings.sensitivity;
    this.input.yaw = this.sim.state.yaw;

    this.dev = new DevTools(this);
    this.devPanel = this.devToolsEnabled ? new DevPanel(dom.app, this.dev, this) : null;
    if (options.startMode && options.startMode !== this.sim.mode) this.sim.setMode(options.startMode);
    this.bindUi();
    this.bindEvents();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(dom.canvas);
    this.renderer.setRenderScale(this.settings.renderScale);
    this.resize();
    this.showOverlay('Click to play. Esc releases the mouse and pauses.');
  }

  // ---------------------------------------------------------------- DevHost

  /** Live tuning: validated new movement values; player, attempt and checkpoint state are kept. Throws on invalid values. */
  applyMovement(movement: Readonly<MovementConfig>): void {
    this.sim.reconfigure(makeConfig({ movement: { ...movement } }, this.config));
    this.dev.configurationChanged();
  }

  rulesChanged(): void {
    this.sim.notifyInterruption('movement rules changed');
    this.dev.configurationChanged();
    this.buildExperimentToggles();
  }

  viewYaw(): number {
    return this.input.yaw;
  }

  resetClock(): void {
    this.clock.reset();
  }

  say(message: string): void {
    this.hud.say(message, performance.now());
  }

  setVectorsVisible(on: boolean): void {
    this.physicsView.visible = on || this.debugVisible;
    this.vectorsVisible = on;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  /** Takes the mouse and resumes. Must be called from a user gesture (click or key press). */
  requestPlay(): Promise<void> {
    return this.input.requestLock();
  }

  /** Gives the mouse back (the pause handler runs once the browser confirms). */
  releasePointer(): void {
    this.input.releaseLock();
  }

  /** R / T from the host's own buttons. */
  restartRun(toStart: boolean): void {
    this.sim.restart(toStart);
  }

  setMode(mode: GameMode): void {
    if (mode !== this.sim.mode) this.sim.setMode(mode);
  }

  /** Applies settings now (sensitivity, view, render scale). Does not persist them. */
  applySettings(settings: Readonly<Settings>): void {
    this.settings = { ...settings };
    this.input.sensitivity = settings.sensitivity;
    this.camera.setFov(settings.fovDeg);
    this.camera.settings.rollDeg = rollDegrees(settings);
    this.renderer.setRenderScale(settings.renderScale);
    // Live: pixel-ratio cap and outline strength. Antialiasing applies at the next load.
    this.renderer.setMaxPixelRatio(QUALITY[settings.graphics].maxPixelRatio);
    this.scene.setHighContrast(settings.highContrast);
    this.scene.setOutlineColor(settings.outlineColor);
  }

  /** Replaces the key table (validated: a conflicting table throws and the old one stays). */
  setBindings(bindings: Readonly<Bindings>): void {
    this.input.setBindings(bindings);
  }

  start(): void {
    const loop = (now: number): void => {
      this.rafId = requestAnimationFrame(loop);
      if (this.fpsLimit > 0) {
        // Frame limiter for validation runs: render on a fixed schedule, never faster.
        const interval = 1000 / this.fpsLimit;
        if (now < this.nextFrameAt - 0.5) return;
        this.nextFrameAt = Math.max(this.nextFrameAt + interval, now - interval);
      }
      this.frameStep(now);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  testHooks(): EngineTestHooks {
    return {
      state: () => {
        const s = this.sim.state;
        return {
          tick: s.tick,
          position: [s.position.x, s.position.y, s.position.z],
          speed: s.planarSpeed(),
          grounded: s.grounded,
          alive: s.alive,
          paused: this.paused,
          mode: this.sim.mode,
          status: this.sim.attempts.status,
          ticksRun: this.ticksRun,
          stalls: this.perf.stallCount,
        };
      },
      // Headless browsers cannot grant pointer lock; tests drive input unlocked.
      forceResume: () => {
        this.input.allowUnlockedInput = true;
        this.resume();
      },
      forcePause: () => this.pause('paused'),
      setYaw: (yaw, pitch) => {
        this.input.yaw = yaw;
        if (pitch !== undefined) this.input.pitch = pitch;
      },
      perf: () => ({ ...this.perf.snapshot(), ...this.renderer.stats() }),
      frameTrace: () => this.trace.samples(),
      clearFrameTrace: () => {
        this.trace.clear();
        this.renderer.gpuTimer?.clear();
      },
      pointerLocked: () => this.input.isLocked,
      look: () => ({ yaw: this.input.yaw, pitch: this.input.pitch, sensitivity: this.input.sensitivity, simYaw: this.sim.state.yaw, tick: this.sim.state.tick }),
      movement: () => ({ ...this.sim.config.movement }),
      waypoints: () => (this.sim.level.data.waypoints ?? []).map((w) => ({ name: w.name, spawn: { position: [...w.spawn.position], yawDeg: w.spawn.yawDeg } })),
      playInputs: (frames, options) => {
        this.restartRun(true);
        this.scripted = frames.map((f) => ({ ...f }));
        this.scriptCursor = 0;
        this.lockstepTicks = Math.max(0, Math.min(64, Math.floor(options?.lockstep ?? 0)));
      },
      scriptedRemaining: () => this.scripted.length - this.scriptCursor,
      gpuTimes: () => (this.renderer.gpuTimer?.available ? [...this.renderer.gpuTimer.samplesMs] : null),
      graphics: () => this.renderer.describe(),
      teleport: (p, v = [0, 0, 0]) => {
        const s = this.sim.state;
        s.position.set(...p);
        s.previousPosition.set(...p);
        s.velocity.set(...v);
        s.grounded = false;
        s.teleported = true;
        this.sim.triggers.resetOccupancy(s.position, s.up);
      },
    };
  }

  /** Adds presentation-only objects (ghosts) to the rendered scene; the caller disposes them. */
  addToScene(object: THREE.Object3D): void {
    this.renderer.scene.add(object);
  }

  dispose(): void {
    cancelAnimationFrame(this.rafId);
    this.resizeObserver.disconnect();
    this.uiAbort.abort();
    for (const u of this.unsubscribe) u();
    this.dev.dispose();
    this.devPanel?.dispose();
    this.input.dispose();
    this.physicsView.dispose();
    this.scene.dispose();
    this.renderer.dispose();
    this.sim.level.physics.dispose();
  }

  // ---------------------------------------------------------------- frame

  private frameStep(now: number): void {
    const frameMs = this.lastFrameMs < 0 ? 0 : now - this.lastFrameMs;
    this.lastFrameMs = now;
    let ticks = 0;
    let alpha = 1;
    const replaying = this.dev.replayActive;
    if (import.meta.env.DEV && this.lockstepTicks > 0 && !this.paused && this.scriptCursor < this.scripted.length) {
      // Lockstep script: a fixed number of ticks per frame, no wall-clock pacing (see EngineTestHooks.playInputs).
      ticks = Math.min(this.lockstepTicks, this.scripted.length - this.scriptCursor);
      for (let i = 0; i < ticks; i++) this.tick(Infinity);
      this.clock.reset();
      if (this.scriptCursor >= this.scripted.length) this.lockstepTicks = 0;
    } else if (!this.paused || replaying) {
      const adv = this.clock.advance(now);
      if (adv.stalled) {
        this.perf.stallCount++;
        this.sim.notifyInterruption(`simulation stall (${Math.round(adv.discardedMs)} ms discarded)`);
      }
      // Replays run while paused (the panel needs the mouse) and at their own speed.
      ticks = replaying ? this.dev.replayBudget(adv.ticks) : adv.ticks;
      alpha = replaying ? 1 : adv.alpha;
      // Each tick takes the input that happened up to the end of its slice of wall-clock time.
      // A replay runs its own number of ticks and ignores live input, so deadlines do not apply.
      for (let i = 0; i < ticks; i++) this.tick(replaying ? Infinity : tickInputDeadline(adv, i, ticks, this.clock.dtMs));
    }
    const r0 = performance.now();
    this.renderFrame(alpha, Math.min(frameMs / 1000, MAX_FRAME_DT));
    const renderMs = performance.now() - r0;
    if (frameMs > 0) this.perf.frame(frameMs, renderMs, ticks);
    if (import.meta.env.DEV) {
      const s = this.sim.state;
      this.trace.push({ t: now, frameMs, ticks, alpha, eyeX: this.eye.x, eyeY: this.eye.y, eyeZ: this.eye.z, yaw: this.input.yaw, speed: s.planarSpeed() });
    }
    this.hud.update(this.sim, now, this.enabledExperimentKeys());
    if (this.debugVisible) this.debugOverlay.update(now, this.sim, this.perf.snapshot(), this.renderer.stats(), this.paused);
    this.devPanel?.update(now);
  }

  private tick(tickEndMs: number): void {
    const physics = this.sim.level.physics;
    physics.resetStats();
    const replayed = this.dev.replayInput() ?? this.nextScripted();
    if (replayed) {
      Object.assign(this.frame, replayed);
      this.input.yaw = replayed.yaw;
      this.input.pitch = replayed.pitch;
      this.input.skipPending();
    } else {
      this.input.sampleAt(this.frame, tickEndMs);
    }
    this.dev.beforeStep(this.frame);
    this.sim.step(this.frame);
    this.dev.afterStep();
    this.onTick?.(this.sim);
    this.ticksRun++;
    this.perf.tick(this.sim.motor.diagnostics.stepMs, physics.stats.queryMs, physics.stats.queries);
  }

  private nextScripted(): InputFrame | null {
    if (!import.meta.env.DEV || this.scriptCursor >= this.scripted.length) return null;
    return this.scripted[this.scriptCursor++]!;
  }

  private renderFrame(alpha: number, dtFrame: number): void {
    const s = this.sim.state;
    const a = s.teleported ? 1 : alpha;
    // Crouching lowers the eye smoothly on the ground; in the air the tuck keeps the head still (Techniques.begin), so it follows at once.
    const eyeTarget = this.sim.motor.techniques.eyeHeight(s);
    this.eyeHeight = s.grounded && !s.teleported ? this.eyeHeight + (eyeTarget - this.eyeHeight) * (1 - Math.exp(-EYE_RATE * dtFrame)) : eyeTarget;
    const eyeH = this.eyeHeight;
    this.eye.x = s.previousPosition.x + (s.position.x - s.previousPosition.x) * a + s.up.x * eyeH;
    this.eye.y = s.previousPosition.y + (s.position.y - s.previousPosition.y) * a + s.up.y * eyeH;
    this.eye.z = s.previousPosition.z + (s.position.z - s.previousPosition.z) * a + s.up.z * eyeH;
    // Look direction comes straight from input so mouse look has no tick latency.
    this.camera.update(this.eye, this.input.yaw, this.input.pitch, s.velocity, dtFrame);
    if (this.physicsView.visible) this.physicsView.update(this.sim);
    if (this.renderer.isContextLost) {
      this.hud.say('Graphics context lost — waiting for the browser to restore it…', performance.now());
      return;
    }
    this.onRender?.(a);
    this.renderer.render(this.camera.camera);
  }

  // ---------------------------------------------------------------- pause

  private pause(reason: string, status = 'Paused. Click to resume.'): void {
    if (this.paused) {
      if (status !== 'Paused. Click to resume.') this.showOverlay(status);
      return;
    }
    this.paused = true;
    this.input.clear();
    this.sim.notifyInterruption(reason);
    this.showOverlay(status);
  }

  private resume(): void {
    // Nothing can be seen without a context, so play waits for the browser to restore it.
    if (!this.paused || this.renderer.isContextLost) return;
    this.paused = false;
    this.input.clear();
    // Time spent paused is not simulated. Nothing moved while paused, so
    // collapse the interpolation interval to avoid a one-tick visual jump back.
    this.clock.reset();
    this.sim.state.previousPosition.copy(this.sim.state.position);
    if (this.dom.overlay) this.dom.overlay.hidden = true;
    this.onPauseChange?.(false, '');
    this.dom.canvas.focus();
  }

  private showOverlay(status: string): void {
    if (this.onPauseChange) {
      this.onPauseChange(true, status);
      return;
    }
    if (this.dom.status) this.dom.status.textContent = this.playtest ? `${this.playtest.banner}
${status}` : status;
    if (this.dom.resume) this.dom.resume.disabled = false;
    if (this.dom.overlay) this.dom.overlay.hidden = false;
  }

  // ---------------------------------------------------------------- commands & events

  private onCommand(action: CommandAction): void {
    if (this.paused && action !== 'toggleDebug' && action !== 'toggleDevPanel') return;
    const now = performance.now();
    switch (action) {
      case 'restart':
        this.sim.restart(false);
        break;
      case 'restartToStart':
        this.sim.restart(true);
        break;
      case 'toggleMode':
        this.sim.setMode(this.sim.mode === 'practice' ? 'full-run' : 'practice');
        this.hud.say(this.sim.mode === 'full-run' ? 'Full run: deaths and pauses invalidate the attempt' : 'Practice: respawn at checkpoints', now);
        break;
      case 'toggleDebug':
        this.debugVisible = !this.debugVisible;
        this.debugOverlay.setVisible(this.debugVisible);
        this.physicsView.visible = this.debugVisible || this.vectorsVisible;
        break;
      case 'toggleDevPanel':
        this.devPanel?.toggle();
        break;
      case 'placeSpawn':
        if (this.devToolsEnabled) this.dev.placeSpawnHere();
        break;
      case 'gotoSpawn':
        if (this.devToolsEnabled) this.dev.gotoSpawn();
        break;
      case 'devBoost':
        if (!import.meta.env.DEV || !this.devToolsEnabled) return;
        this.devBoost();
        this.hud.say('Dev boost (attempt invalidated in full run)', now);
        break;
    }
  }

  private devBoost(): void {
    const s = this.sim.state;
    this.sim.notifyInterruption('dev boost');
    s.velocity.x += -Math.sin(this.input.yaw) * DEV_BOOST_SPEED;
    s.velocity.z += -Math.cos(this.input.yaw) * DEV_BOOST_SPEED;
  }

  private bindEvents(): void {
    const ev = this.sim.events;
    const now = (): number => performance.now();
    this.unsubscribe.push(
      ev.on('death', (e) => {
        this.hud.flash('death');
        this.hud.say(e.mode === 'full-run' ? 'Died — attempt failed. R to restart.' : `Died (${e.cause})`, now());
      }),
      ev.on('checkpoint', (e) => {
        this.hud.flash('checkpoint');
        this.hud.say(`Checkpoint ${e.index + 1}${e.split !== null ? ` · ${formatTime(e.split)}` : ''}`, now());
      }),
      ev.on('finish', (e) => {
        this.hud.flash('finish');
        const tag = !e.valid ? ' (practice — not recorded)' : e.newBest ? ' · new best!' : '';
        this.hud.say(`Finish ${formatTime(e.seconds)}${tag}`, now());
      }),
      ev.on('attempt-invalidated', (e) => this.hud.say(`Attempt invalidated: ${e.reason}`, now())),
      ev.on('start-refused', (e) => this.hud.say(startRefusedText(e), now())),
    );
  }

  // ---------------------------------------------------------------- settings / UI

  private bindUi(): void {
    const { inputs, resume } = this.dom;
    const opts = { signal: this.uiAbort.signal };
    // The renderer's own listener (registered first) marks the context lost; a full run cannot continue unseen.
    this.dom.canvas.addEventListener(
      'webglcontextlost',
      () => {
        this.pause('graphics context lost', 'Graphics context lost. Waiting for the browser to restore it…');
        // Free the mouse: a lock taken while the context is gone would otherwise never resume the game.
        this.input.releaseLock();
      },
      opts,
    );
    this.dom.canvas.addEventListener(
      'webglcontextrestored',
      () => {
        if (!this.paused) return;
        // The player clicked resume while the context was lost: the lock is already held, so continue now.
        if (document.pointerLockElement === this.dom.canvas) this.resume();
        else this.showOverlay('Graphics restored. Click to resume.');
      },
      opts,
    );
    resume?.addEventListener(
      'click',
      () => {
        this.input.requestLock().catch(() => {
          // onLockError already explained the failure in the overlay.
        });
      },
      opts,
    );
    if (this.playtest) {
      const exit = this.playtest.onExit;
      // Esc while paused (the first Esc already released the mouse) goes back to the editor.
      document.addEventListener('keydown', (e) => e.code === 'Escape' && this.paused && !e.repeat && exit(), opts);
    }
    this.buildExperimentToggles();
    if (!inputs) return;
    inputs.sensitivity.value = String(this.settings.sensitivity);
    inputs.fov.value = String(this.settings.fovDeg);
    inputs.scale.value = String(this.settings.renderScale);
    inputs.roll.checked = this.settings.roll;
    const apply = (): void => {
      this.applySettings({
        ...this.settings,
        sensitivity: Number(inputs.sensitivity.value),
        fovDeg: Number(inputs.fov.value),
        renderScale: Number(inputs.scale.value),
        roll: inputs.roll.checked,
      });
      saveSettings(this.settings);
    };
    for (const el of [inputs.sensitivity, inputs.fov, inputs.scale, inputs.roll]) el.addEventListener('input', apply, opts);
  }

  private buildExperimentToggles(): void {
    const host = this.dom.experiments;
    if (!host) return;
    host.replaceChildren();
    for (const exp of EXPERIMENTS) {
      const label = document.createElement('label');
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.dataset['experiment'] = exp.key;
      box.checked = isExperimentEnabled(this.rules, exp.key);
      box.addEventListener('change', () => this.toggleExperiment(exp, box.checked));
      label.append(box, ` ${exp.key}: ${exp.name}`);
      host.append(label);
    }
  }

  private toggleExperiment(exp: ExperimentInfo, on: boolean): void {
    setExperimentEnabled(this.rules, exp.key, on);
    this.dev.configurationChanged();
    // The record key changed: start a fresh attempt from the track start.
    this.sim.restart(true);
  }

  private enabledExperimentKeys(): string {
    return EXPERIMENTS.filter((e) => isExperimentEnabled(this.rules, e.key))
      .map((e) => e.key)
      .join('');
  }

  private resize(): void {
    const { width, height } = this.renderer.resize();
    this.camera.resize(width, height);
  }
}

